import { useState } from "react"
import api from "../../lib/api"
import { getErrorMessage } from "../../lib/errors"

export type GroupCredentialPanelProps = {
  /** Omitido cuando el grupo se gestiona desde "Grupos" en general, sin un semillero de origen en la URL. */
  seedbedId?: string
  groupId: string
  /** login_code ya existente (de una sesión previa) — se muestra sin la contraseña, que nunca se vuelve a mostrar. */
  existingLoginCode?: string | null
}

type Credential = { login_code: string; password: string }

export function GroupCredentialPanel({ seedbedId, groupId, existingLoginCode }: GroupCredentialPanelProps) {
  const [credential, setCredential] = useState<Credential | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const basePath = seedbedId
    ? `/academy/seedbeds/admin/${seedbedId}/groups/${groupId}/credential`
    : `/academy/seedbeds/admin/groups/${groupId}/credential`

  async function generateOrReset() {
    setLoading(true)
    setError("")
    try {
      const res = await api.post(basePath)
      setCredential(res.data.data)
    } catch (err) {
      // Ya existe una credencial: resetearla en su lugar.
      try {
        const res = await api.post(`${basePath}/reset`)
        setCredential(res.data.data)
      } catch (resetErr) {
        setError(getErrorMessage(resetErr) || getErrorMessage(err))
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 dark:border-amber-500/30 dark:bg-amber-500/5 space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-800 dark:text-white/90">Credencial de cuenta grupal</h4>
        <button
          type="button"
          onClick={() => void generateOrReset()}
          disabled={loading}
          className="rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-600 disabled:opacity-60"
        >
          {loading ? "Generando..." : credential || existingLoginCode ? "Resetear credencial" : "Generar credencial"}
        </button>
      </div>

      {error && <p className="text-xs text-error-600 dark:text-error-400">{error}</p>}

      {credential ? (
        <div className="rounded-lg border border-amber-300 bg-white p-3 text-sm dark:border-amber-700 dark:bg-gray-900">
          <p className="text-amber-700 dark:text-amber-400 text-xs font-medium mb-2">
            Guarda esta contraseña ahora: no se volverá a mostrar.
          </p>
          <dl className="space-y-1">
            <div className="flex justify-between gap-2">
              <dt className="text-gray-500">Código de acceso</dt>
              <dd className="font-mono text-gray-900 dark:text-white">{credential.login_code}</dd>
            </div>
            <div className="flex justify-between gap-2">
              <dt className="text-gray-500">Contraseña</dt>
              <dd className="font-mono text-gray-900 dark:text-white">{credential.password}</dd>
            </div>
          </dl>
        </div>
      ) : existingLoginCode ? (
        <div className="rounded-lg border border-amber-300 bg-white p-3 text-sm dark:border-amber-700 dark:bg-gray-900">
          <div className="flex justify-between gap-2">
            <dt className="text-gray-500">Código de acceso</dt>
            <dd className="font-mono text-gray-900 dark:text-white">{existingLoginCode}</dd>
          </div>
          <p className="mt-2 text-xs text-gray-500">
            Ya existe una contraseña para este código, pero no se puede volver a mostrar. Usa "Resetear credencial" para generar una nueva.
          </p>
        </div>
      ) : (
        <p className="text-xs text-gray-500">
          Este grupo aún no tiene credencial de acceso — genera una para que el equipo pueda entrar con un solo usuario y contraseña.
        </p>
      )}
    </div>
  )
}

export default GroupCredentialPanel
